"use client";

/**
 * An event's page following ?lista-de-espera: where the sign-in link asked for
 * from a full event's waiting list lands (public-api's /auth/login-link with
 * &lista=1). Once the visitor's own state is in, the query is taken off the
 * address and the waiting list opens, one tap from being on it -- unless they
 * are going, are on it already, or the event takes no registrations.
 */
import { useEffect } from "react";
import { WAITING_LIST_PARAM } from "@/lib/address";
import { useSession } from "@/lib/session";
import type { EventWithState } from "@/lib/types/event";
import { openWaitingList } from "@/components/waiting/waitingList";

const withoutWaitingListParam = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete(WAITING_LIST_PARAM);
  const search = params.toString();
  return `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
};

/** `ready` says the visitor's own state has been asked for. */
export function WaitingListOpener({ event, ready = true }: { event: EventWithState; ready?: boolean }) {
  const session = useSession();
  const signedIn = !!session.client;
  // a signed-in client's answer carries it; until it does, theirs is not in yet
  const ownStateIn = event.is_registered !== undefined;
  const canJoin = event.registration_state === "OPEN" && !event.is_registered && !Number(event.on_waiting_list);

  useEffect(() => {
    if (!ready || !session.ready || (signedIn && !ownStateIn)) return;
    if (!new URLSearchParams(window.location.search).has(WAITING_LIST_PARAM)) return;
    window.history.replaceState(window.history.state, "", withoutWaitingListParam());
    if (signedIn && canJoin) {
      // once the page itself has been shown, so the sheet is not left underneath it
      setTimeout(() => openWaitingList(event), 50);
    }
    // the event's id decides; the rest of it is read once the state is in
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, ready, session.ready, signedIn, ownStateIn, canJoin]);

  return null;
}
